import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MessageSquare, Send } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import client from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-3xl px-6 pt-14 pb-16 md:pt-20">
        <div className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-ho-primary">Contact</span>
          <h1 className="font-display mt-4 text-4xl font-black tracking-tighter md:text-5xl">Let's talk</h1>
          <p className="mx-auto mt-4 max-w-md text-lg text-ho-muted">
            Questions, partnerships, or just want to say hi? Drop us a note.
          </p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mt-10 rounded-[32px] border border-black/5 bg-white p-6 md:p-8">
          {sent ? (
            <div className="py-10 text-center">
              <div className="mx-auto grid place-items-center w-14 h-14 rounded-full bg-ho-primary/10">
                <MessageSquare className="w-7 h-7 text-ho-primary" />
              </div>
              <h3 className="font-display mt-4 text-xl font-bold">Thanks — message received!</h3>
              <p className="mt-2 text-ho-muted">We usually reply within one business day.</p>
              <button onClick={() => setSent(false)} data-testid="contact-again"
                className="tap-scale mt-6 rounded-full bg-ho-ink px-6 py-3 text-sm font-semibold text-white">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <Field label="Your name">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name" placeholder="Jane Doe"
                  className="h-13 w-full rounded-2xl border border-black/10 bg-ho-bg px-4 py-3.5 outline-none focus:border-ho-primary" />
              </Field>
              <Field label="Email">
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email" placeholder="you@email.com"
                  className="h-13 w-full rounded-2xl border border-black/10 bg-ho-bg px-4 py-3.5 outline-none focus:border-ho-primary" />
              </Field>
              <Field label="Message">
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message" placeholder="How can we help?"
                  className="w-full rounded-2xl border border-black/10 bg-ho-bg px-4 py-3.5 outline-none focus:border-ho-primary resize-none" />
              </Field>
              <button type="submit" disabled={busy} data-testid="contact-submit"
                className="tap-scale inline-flex w-full items-center justify-center gap-2 rounded-full bg-ho-primary py-4 font-semibold text-white disabled:opacity-60">
                {busy ? "Sending…" : <>Send message <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </motion.div>

        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-ho-muted">
          <Mail className="w-4 h-4" /> hello@handson.build
        </div>
      </section>
    </MarketingLayout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-semibold text-ho-ink">{label}</span>
    {children}
  </label>
);
